import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Search,
  Link,
  ArrowRight,
  FileText,
  Video,
  Image,
  Music,
  File,
  ExternalLink,
  Trash2
} from 'lucide-react';
import { useRelatedRecords } from '../../hooks/useRelatedRecords';
import {
  RelationshipNode,
  RelatedRecord,
  RelationshipForm,
  RelationshipType,
  MediaType
} from '../../types/mediaRecord';

interface RelatedRecordsProps {
  mediaId: string;
  className?: string;
}

const getMediaTypeIcon = (mediaType: MediaType) => {
  switch (mediaType) {
    case 'video': return Video;
    case 'audio': return Music;
    case 'image': return Image;
    case 'document': return FileText;
    default: return File;
  }
};

const getRelationshipColor = (type: RelationshipType) => {
  const colors = {
    parent: 'text-blue-400 bg-blue-400/10',
    child: 'text-green-400 bg-green-400/10',
    version: 'text-purple-400 bg-purple-400/10',
    derivative: 'text-orange-400 bg-orange-400/10',
    reference: 'text-gray-400 bg-gray-400/10',
    translation: 'text-pink-400 bg-pink-400/10',
    remix: 'text-yellow-400 bg-yellow-400/10',
    cover: 'text-indigo-400 bg-indigo-400/10',
    sample: 'text-red-400 bg-red-400/10',
    collaboration: 'text-cyan-400 bg-cyan-400/10',
    series: 'text-teal-400 bg-teal-400/10',
    collection: 'text-violet-400 bg-violet-400/10',
    alternate: 'text-amber-400 bg-amber-400/10',
    source: 'text-emerald-400 bg-emerald-400/10',
    adaptation: 'text-rose-400 bg-rose-400/10',
    inspiration: 'text-sky-400 bg-sky-400/10'
  };
  return colors[type] || 'text-gray-400 bg-gray-400/10';
};

const relationshipTypes: RelationshipType[] = [
  'parent', 'child', 'version', 'derivative', 'reference', 'translation',
  'remix', 'cover', 'sample', 'collaboration', 'series', 'collection',
  'alternate', 'source', 'adaptation', 'inspiration'
];

interface TreeNodeProps {
  node: RelationshipNode;
  depth: number;
  onRemoveRelationship: (relationshipId: string) => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, depth, onRemoveRelationship }) => {
  const [isExpanded, setIsExpanded] = useState(depth < 2);
  const hasChildren = node.children.length > 0;
  const IconComponent = getMediaTypeIcon(node.media_type);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: depth * 0.1 }}
      className={`${depth > 0 ? 'ml-6 border-l border-white/10 pl-4' : ''}`}
    >
      <div className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg transition-colors group">
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <div className="flex-shrink-0 w-6 h-6"></div>
        )}

        {/* Media Type Icon */}
        <div className="flex-shrink-0">
          <IconComponent className="w-5 h-5 text-gray-400" />
        </div>

        {/* Node Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-white truncate">{node.title}</h4>
            <span className="text-xs text-gray-400 bg-white/10 px-2 py-1 rounded">
              {node.format}
            </span>
          </div>

          {/* Relationships */}
          <div className="flex flex-wrap gap-1 mt-1">
            {node.relationships.map((rel, idx) => (
              <span
                key={idx}
                className={`text-xs px-2 py-1 rounded-full ${getRelationshipColor(rel.type)}`}
              >
                {rel.type}
                {rel.subtype && `: ${rel.subtype}`}
              </span>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(`/media/${node.id}`, '_blank')}
              className="text-gray-400 hover:text-blue-400 transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            {depth > 0 && (
              <button
                onClick={() => onRemoveRelationship(node.id)}
                className="text-gray-400 hover:text-red-400 transition-colors"
                title="Remove relationship"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Children */}
      <AnimatePresence>
        {isExpanded && hasChildren && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                node={child}
                depth={depth + 1}
                onRemoveRelationship={onRemoveRelationship}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

interface AddRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (form: RelationshipForm) => void;
  isLoading: boolean;
}

const AddRelationshipModal: React.FC<AddRelationshipModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  isLoading
}) => {
  const [form, setForm] = useState<RelationshipForm>({
    target_media_id: '',
    relationship_type: 'reference',
    strength: 1.0,
    bidirectional: false
  });
  const [searchTerm, setSearchTerm] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.target_media_id.trim()) {
      onAdd(form);
      setForm({
        target_media_id: '',
        relationship_type: 'reference',
        strength: 1.0,
        bidirectional: false
      });
      setSearchTerm('');
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="cosmic-glass rounded-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Add Relationship</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Search for Target Media */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Target Media ID
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={form.target_media_id}
                onChange={(e) => setForm(prev => ({ ...prev, target_media_id: e.target.value }))}
                className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 outline-none focus:border-blue-400 transition-colors"
                placeholder="Enter media ID or search..."
                required
              />
            </div>
          </div>

          {/* Relationship Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Relationship Type
            </label>
            <select
              value={form.relationship_type}
              onChange={(e) => setForm(prev => ({ ...prev, relationship_type: e.target.value as RelationshipType }))}
              className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white outline-none focus:border-blue-400 transition-colors"
            >
              {relationshipTypes.map(type => (
                <option key={type} value={type} className="bg-gray-800">
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Relationship Subtype */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Subtype (Optional)
            </label>
            <input
              type="text"
              value={form.relationship_subtype || ''}
              onChange={(e) => setForm(prev => ({ ...prev, relationship_subtype: e.target.value }))}
              className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 outline-none focus:border-blue-400 transition-colors"
              placeholder="e.g., 'instrumental', 'extended version'"
            />
          </div>

          {/* Strength */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Relationship Strength: {form.strength}
            </label>
            <input
              type="range"
              min="0.1"
              max="1.0"
              step="0.1"
              value={form.strength}
              onChange={(e) => setForm(prev => ({ ...prev, strength: parseFloat(e.target.value) }))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Weak (0.1)</span>
              <span>Strong (1.0)</span>
            </div>
          </div>

          {/* Bidirectional */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="bidirectional"
              checked={form.bidirectional}
              onChange={(e) => setForm(prev => ({ ...prev, bidirectional: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-600 bg-white/5 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
            />
            <label htmlFor="bidirectional" className="text-sm text-gray-300">
              Bidirectional relationship
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={isLoading || !form.target_media_id.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Link className="w-4 h-4" />
              {isLoading ? 'Adding...' : 'Add Relationship'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export const RelatedRecords: React.FC<RelatedRecordsProps> = ({ mediaId, className }) => {
  const {
    data: records,
    tree,
    isLoading,
    error,
    addRelationship,
    removeRelationship
  } = useRelatedRecords(mediaId, {
    maxDepth: 3,
    includeReverse: true,
    limit: 100
  });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddingRelationship, setIsAddingRelationship] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  const handleAddRelationship = async (form: RelationshipForm) => {
    setIsAddingRelationship(true);
    try {
      await addRelationship(form);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Failed to add relationship:', error);
    } finally {
      setIsAddingRelationship(false);
    }
  };

  const handleRemoveRelationship = async (relationshipId: string) => {
    if (confirm('Are you sure you want to remove this relationship?')) {
      try {
        await removeRelationship(relationshipId);
      } catch (error) {
        console.error('Failed to remove relationship:', error);
      }
    }
  };

  const filteredRecords = records.filter(record =>
    record.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
    record.relationship.type.toLowerCase().includes(searchFilter.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className={`cosmic-glass rounded-lg p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-4 w-4 bg-white/10 rounded"></div>
                <div className="h-4 bg-white/10 rounded flex-1"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className={`cosmic-glass rounded-lg overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Related Records</h2>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Relationship
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 outline-none focus:border-blue-400 transition-colors"
            placeholder="Search relationships..."
          />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error ? (
          <div className="text-center text-red-400">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Failed to load relationships</p>
            <p className="text-sm opacity-75">{error.message}</p>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="text-center text-gray-400">
            <Link className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No relationships found</p>
            <p className="text-sm opacity-75">
              {searchFilter ? 'Try adjusting your search terms' : 'Add relationships to see connections between media records'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Tree View */}
            {tree.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-white mb-3">Relationship Tree</h3>
                {tree.map((node) => (
                  <TreeNode
                    key={node.id}
                    node={node}
                    depth={0}
                    onRemoveRelationship={handleRemoveRelationship}
                  />
                ))}
              </div>
            )}

            {/* List View */}
            {searchFilter && (
              <div>
                <h3 className="text-lg font-medium text-white mb-3">Search Results</h3>
                <div className="space-y-2">
                  {filteredRecords.map((record) => {
                    const IconComponent = getMediaTypeIcon(record.media_type);
                    return (
                      <motion.div
                        key={record.media_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg transition-colors group"
                      >
                        <IconComponent className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-white truncate">{record.title}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-1 rounded-full ${getRelationshipColor(record.relationship.type)}`}>
                              {record.relationship.type}
                              {record.relationship.subtype && `: ${record.relationship.subtype}`}
                            </span>
                            <span className="text-xs text-gray-400">
                              Depth: {record.relationship.depth}
                            </span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => window.open(`/media/${record.media_id}`, '_blank')}
                            className="text-gray-400 hover:text-blue-400 transition-colors"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        {records.length > 0 && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-400">{records.length}</div>
                <div className="text-xs text-gray-400">Total Relations</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400">
                  {new Set(records.map(r => r.relationship.type)).size}
                </div>
                <div className="text-xs text-gray-400">Relation Types</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-400">
                  {Math.max(...records.map(r => r.relationship.depth), 0)}
                </div>
                <div className="text-xs text-gray-400">Max Depth</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Relationship Modal */}
      <AnimatePresence>
        <AddRelationshipModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleAddRelationship}
          isLoading={isAddingRelationship}
        />
      </AnimatePresence>
    </motion.div>
  );
};

export default RelatedRecords;